import React, { useCallback, useMemo, useState } from "react";
import {
  LocationOn as LocationOnIcon,
  QueryBuilder as QueryBuilderIcon,
  PropaneTank as PropaneTankIcon,
  LocalShipping as LocalShippingIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

import { EOrderStatusFinally } from "enums/options/orders";
import {
  OrderProps,
  OrderUpdateActions,
} from "store/modules/Orders/types.store";
import { useTranslation } from "react-i18next";
import { addMinutes, format } from "date-fns";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import { formatStreetAddress } from "utils/formatByText";

import * as Styled from "./styles";
import { handleGetDealerDriversRequest } from "store/modules/DealerUsers/thunk.store";
import { useAppDispatch } from "store/hooks";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import { handleAddDriveOrderRequest } from "store/modules/Orders/thunk.store";
import {
  canShowDriverAction,
  getOrderDriverPermissions,
  orderHasAssignedDriver,
  resolveCoverageAreaMapType,
} from "pages/ManageOrders/components/CardOrderDetails/OrderDriverSection/getOrderDriverPermissions";

interface CardAccordionProps {
  data: OrderProps;
  handleOrderUpdate: (id: string, consumerId: string, select?: string) => void;
  ordersSelected?: number | null;
  indexSelected?: number;
  pinSelected?: boolean;
  handleOrderAccept?: (
    type: OrderUpdateActions,
    id: string,
    dealerId: string,
  ) => void;
}

const DEFAULT_ETA = 60;

const CardOrderTracking: React.FC<CardAccordionProps> = ({
  data,
  handleOrderUpdate,
  pinSelected,
  indexSelected,
  ordersSelected,
  handleOrderAccept,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [addDrive, setAddDrive] = useState<boolean>(false);
  const [allIUsers, setAllIUsers] = useState<DealerUsersProps[]>();
  const [driveSelected, setDriveSelected] = useState<{
    label: string;
    id: string;
  }>();

  const handleAddDrive = useCallback(async () => {
    if (addDrive) {
      await dispatch(
        handleAddDriveOrderRequest({
          dealerId: data?.dealer?.id || "",
          orderId: data?.id || "",
          driverName: driveSelected?.label || "",
          driverId: driveSelected?.id || "",
        }),
      ).unwrap;
      setAddDrive(false);
    }
  }, [
    data?.dealer?.id,
    data?.id,
    driveSelected?.id,
    dispatch,
    driveSelected?.label,
    addDrive,
  ]);

  const IUsers = useMemo(() => {
    return allIUsers?.map((x: DealerUsersProps) => ({
      label: `${x?.name ?? ""} ${x?.surname ?? ""}`.trim(),
      id: x?.id,
    }));
  }, [allIUsers]);

  const timeFinalized = data.timeline.find(
    (value) => value.description === data.status,
  )?.occurredAt;

  const getFinalizedTimeLabel = (): string => {
    if (timeFinalized) {
      return ` ${format(new Date(timeFinalized || ""), "HH:mm")}`;
    }
    if (data?.delivery?.eta?.initial) {
      return format(
        addMinutes(new Date(data?.delivery?.eta?.initial || ""), DEFAULT_ETA),
        "HH:mm",
      );
    }
    return t("undefined");
  };

  const handledUsers = async () => {
    const response = await dispatch(
      handleGetDealerDriversRequest({
        dealerId: data?.dealer.id || "",
        limit: 200,
      }),
    ).unwrap();

    setAllIUsers(response?.data);
  };

  const mapType = resolveCoverageAreaMapType({
    placedOrigin: data?.placedOrigin,
  });
  const hasOrderDriver = orderHasAssignedDriver(data?.driver);
  const driverPermissions = getOrderDriverPermissions({
    mapType,
    hasOrderDriver,
  });
  const canShowDriverActionButton = canShowDriverAction(
    driverPermissions,
    hasOrderDriver,
  );

  return (
    <Styled.Container
      pinSelected={pinSelected}
      colorSelected={ordersSelected === indexSelected}
    >
      <Styled.CardContent
        onClick={() => {
          return handleOrderUpdate(data.id, data.consumer.id);
        }}
      >
        <Styled.ContainerColumn>
          <Styled.Content>
            <Styled.ContentRow>
              <Styled.NameCard variant="body1">
                {data?.consumer?.name}
              </Styled.NameCard>
              <Styled.ContainerArrow>
                {!!data.timeline.find(
                  (value) =>
                    value.description === EOrderStatusFinally.DRIVERONTHEWAY,
                ) && (
                  <Styled.ContainerStatus colorTheme="primary">
                    <Typography
                      color="rgba(0, 0, 0, 0.87)"
                      variant="caption"
                      fontSize={14}
                    >
                      Entrega em curso
                    </Typography>
                  </Styled.ContainerStatus>
                )}
                <Styled.ButtonSelected
                  onClick={() => {
                    return handleOrderUpdate(
                      data.id,
                      data.consumer.id,
                      "select",
                    );
                  }}
                >
                  <ArrowForwardIcon color="action" />
                </Styled.ButtonSelected>
              </Styled.ContainerArrow>
            </Styled.ContentRow>

            <Styled.NameAdress
              fontSize="14px"
              textAlign="start"
              variant="subtitle2"
            >
              <LocationOnIcon fontSize="medium" />{" "}
              {formatStreetAddress(data?.delivery?.address)}
            </Styled.NameAdress>

            <Styled.NameLocale variant="caption" textAlign="start">
              {data?.delivery?.address?.district}
            </Styled.NameLocale>

            <Styled.ContentRow>
              {data.dealerState !== "Finalized" && (
                <Styled.NameAdress variant="subtitle2">
                  <QueryBuilderIcon
                    fontSize="small"
                    sx={{ marginTop: "-2px" }}
                  />
                  Entregar em{" "}
                  {data?.delivery?.eta?.initial
                    ? ` (${data?.delivery?.eta?.initial} ${t("minutes")})`
                    : `${DEFAULT_ETA} ${t("minutes")}`}
                </Styled.NameAdress>
              )}

              {data.dealerState === "Finalized" && (
                <Styled.NameAdress variant="subtitle2">
                  <QueryBuilderIcon
                    fontSize="small"
                    sx={{ marginTop: "-2px" }}
                  />
                  Finalizado às
                  {getFinalizedTimeLabel()}
                </Styled.NameAdress>
              )}

              <Styled.NameAdress variant="subtitle2">
                <PropaneTankIcon
                  color="primary"
                  fontSize="small"
                  sx={{ marginTop: "-2px" }}
                />
                {data.items.length === 1
                  ? data.items[0].description
                  : "Vários produtos"}
              </Styled.NameAdress>
            </Styled.ContentRow>

            {data.dealerState === "New" && <Styled.DividerContainer />}
          </Styled.Content>
        </Styled.ContainerColumn>
      </Styled.CardContent>

      {data?.dealerState === "New" && !addDrive && (
        <Styled.ContainerDrive>
          <Styled.ContainerDriverSelected>
            <LocalShippingIcon fontSize="small" color="inherit" />
            <Styled.ContainerText>
              <Typography variant="subtitle2" textAlign="start">
                {data?.driver?.name
                  ? `${data?.driver?.name} ${data?.driver?.surname ?? ""}`
                  : t("undefined")}
              </Typography>
            </Styled.ContainerText>
          </Styled.ContainerDriverSelected>

          {canShowDriverActionButton && (
            <Styled.Button
              type="button"
              onClick={() => {
                setAddDrive(true);
                handledUsers();
              }}
            >
              <Styled.ButtonAccept color="inherit">
                {hasOrderDriver ? "TROCAR" : " SELECIONAR ENTREGADOR"}
              </Styled.ButtonAccept>
            </Styled.Button>
          )}
        </Styled.ContainerDrive>
      )}
      {data?.dealerState === "New" && addDrive && canShowDriverActionButton && (
        <Styled.ContainerAutoComplete>
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={IUsers || []}
            size="small"
            onChange={(_, e) => setDriveSelected(e as any)}
            renderInput={(params) => (
              <TextField {...params} label={t("selectDriver")} />
            )}
            sx={{
              width: 200,
            }}
          />

          <Grid>
            <Styled.ContainerButtonsDrive>
              <Styled.ButtonBackDrive
                type="button"
                onClick={() => setAddDrive(false)}
              >
                <Typography variant="button" textAlign="start">
                  Sair
                </Typography>
              </Styled.ButtonBackDrive>
              <Styled.ButtonApplyDrive
                type="button"
                onClick={() => handleAddDrive()}
              >
                <Typography variant="button" textAlign="start">
                  Aplicar
                </Typography>
              </Styled.ButtonApplyDrive>
            </Styled.ContainerButtonsDrive>
          </Grid>
        </Styled.ContainerAutoComplete>
      )}
      {data.dealerState === "New" && (
        <Styled.ButtonAcceptOrder
          onClick={() =>
            handleOrderAccept &&
            handleOrderAccept("accept", data?.id || "", data?.dealer.id || "")
          }
          color="error"
        >
          ACEITAR PEDIDO
        </Styled.ButtonAcceptOrder>
      )}
    </Styled.Container>
  );
};

export default CardOrderTracking;
